import { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import DataTable, { createTheme } from "react-data-table-component";
import { getColumns, Attributes } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import { telemetry } from "globals/endpoints";
import EditIcon from "@mui/icons-material/Edit";
import CTLBaseDialogForm from "application/components/CTLBaseDialogForm";
import CTLNotification from "application/components/CTLNotification";
import DeleteConfirmation from "application/components/DeleteConfirmation";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { FullDetails } from "./model/fullDetails";

function eamsService({ onLoad, registerCallBacks, pageName }) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [ViewData, setViewData] = useState([]);
  const [ViewSingleData, setViewSingleData] = useState([]);
  const [openDetails, setOpenDetails] = useState(false);
  const darkModeContext = useMaterialUIController();
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateList, setUpdateList] = useState(Date.now());
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const URL = {
    list: `/eams/system-info`,
  };

  const URLSAVE = {
    list: `/eams/device-info`,
  };

  function GetData() {
    telemetry
      .get(URL.list)
      .then((res) => {
        setViewData(res.data.data);
      })
      .catch((error) => {
        console.log("error while the call /eams/system-info", error);
      });
  }

  // Returns the API endpoint for saving external device info for a specific macAddress
  function SaveData(macAddress) {
    return `${URLSAVE.list}/${macAddress}`;
  }

  function GetSingleData(macAddress) {
    telemetry
      .get(`${URL.list}/${macAddress}`)
      .then((res) => {
        setViewSingleData(res.data.data);
        setOpenDetails(true);
      })
      .catch((error) => {
        console.log(
          "error while the call /eams/system-info/" + macAddress,
          error
        );
      });
  }

  function DeleteData(macAddress) {
    telemetry
      .delete(`${URL.list}/${macAddress}`)
      .then((res) => {
        setNotify({
          isOpen: true,
          message: res.data.message || "Deleted successfully",
          type: "success",
          pagename: pageName,
          status: res.data.messageDetail,
        });
        GetData();
      })
      .catch((error) => {
        console.log(
          "error while the call /eams/system-info/" + macAddress,
          error
        );
      });
  }

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        openForm: onOpenForm,
      });
  }, [activePage]);

  useEffect(() => {
    if (open) {
      GetData();
    } else {
      setViewData([]);
    }
  }, [open]);

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onOpenForm(row) {
    const tryParseJSON = (value) => {
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return value;
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        return value;
      }
    };

    // Pre-fill the dialog form with the selected row so it can include macAddress on save.
    // CTLBaseDialogForm will only send fields marked allowInForm, so we merge any row fields here.
    const hardwareFromRow = tryParseJSON(row?.hardwareComponentJson);
    const hardwareFields =
      hardwareFromRow && typeof hardwareFromRow === "object" && !Array.isArray(hardwareFromRow)
        ? hardwareFromRow
        : {};

    setFormData({
      ...(row || {}),
      ...hardwareFields,
      eventTime: Date.now(),
    });
    setOpenForm(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  const editRow = (row) => {
    setFormData(row);
    setOpenForm(true);
  };

  const deleteRow = (row) => {
    setSelectedRow(row);
    setConfirmDelete(true);
  };

  const onCancel = () => {
    setSelectedRow([]);
    setConfirmDelete(false);
  };

  const onAgree = (row) => {
    if (!row?.macAddress) return;

    telemetry
      .post(SaveData(row.macAddress), {
        intId: [row.id],
      })
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          message: response.data.message,
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
        GetSingleData(row.macAddress);
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          pagename: pageName,
          status: error?.response?.data?.messageDetail,
        });
      })
      .finally(() => {
        setConfirmDelete(false);
      });
  };

  function onSaveForm(row) {
    // CTLBaseDialogForm sends an object like { id, FIELD1: value1, FIELD2: value2 }
    // Backend expects Map<String, String> and macAddress as path variable.
    const macAddress = formData?.macAddress || row?.macAddress;
    if (!macAddress) return;

    const isNonEmpty = (v) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "string") return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "object") return Object.keys(v).length > 0;
      return true;   
    };

    const allowedKeys = (getColumns(() => {}, () => {}) || [])
      .filter((c) => c?.allowInForm)
      .map((c) => c.accessor)
      .filter(Boolean);

    const payload = Object.fromEntries(
      allowedKeys
        .map((k) => [k, row?.[k]])
        .filter(([, v]) => isNonEmpty(v))
        .map(([k, v]) => [k, String(v)])
    );

    telemetry
      .post(SaveData(macAddress), payload)
      .then((response) => {
        setUpdateList(Date.now());
        setNotify({
          isOpen: true,
          message: response.data.message,
          type: "success",
          pagename: pageName,
          status: response.data.messageDetail,
        });
        GetSingleData(macAddress);
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          pagename: pageName,
          status: error?.response?.data?.messageDetail,
        });
      })
      .finally(() => {
        setOpenForm(false);
      });
  }

  function onCloseForm() {
    setOpenForm(false);
  }

  const header = [
    {
      name: "Mac Address",
      selector: (row) => row?.macAddress || "-",
      sortable: true,
      grow: 3,
      style: {
        justifyContent: "flex-start",
      },
    },
    {
      name: "Ip Address",
      selector: (row) => row?.ipAddress || "-",
      sortable: true,
      grow: 2,
      style: {
        justifyContent: "flex-start",
      },
    },
    {
      name: "Username",
      selector: (row) => row?.username || "Unkown user",
      sortable: true,
      grow: 2,
      style: {
        justifyContent: "flex-start",
      },
    },
    {
      name: "Action",
      cell: (row) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="info"
              onClick={() => GetSingleData(row.macAddress)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => DeleteData(row.macAddress)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
      center: true,
    },
  ];

  const tableCustomStyles = {
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
  };

  return (
    <MDBox>
      <DeleteConfirmation
        entityName="EAMS Service"
        data={selectedRow}
        columns={getColumns(deleteRow, editRow)}
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
      />
      <CTLBaseDialogForm
        entityName="EAMS Service"
        columns={getColumns(deleteRow, editRow)}
        openForm={openForm}
        onSaveForm={onSaveForm}
        onCloseForm={onCloseForm}
        formData={formData}
        pageAttributes={Attributes}
      />

      <CTLAdvancedFilter
        entityName="EAMS Service"
        columns={getColumns(deleteRow, editRow)}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
      />

      <FullDetails
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        data={ViewSingleData}
        title={`EAMS System Information - ${ViewSingleData?.macAddress || ""}`}
        onEditExternalHardware={(row) => {
          onOpenForm(row);
        }}
      />

      <DataTable
        columns={header}
        data={ViewData}
        canSearch
        responsive
        pagination
        striped
        paginationPerPage={20}
        paginationRowsPerPageOptions={[5, 10, 20]}
        theme={darkModeContext[0].darkMode ? "modifiedDarkTheme" : "default"}
        customStyles={tableCustomStyles}
        fixedHeader
        persistTableHead
        fixedHeaderScrollHeight={`${(window.innerHeight / 100) * 66}px`}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

export default eamsService;
